// pillarReadiness.js — partial-load honesty for the pillar scores (2026-10-23).
// On a slow connection the inputs land one by one and a score computed from a
// partial set is not a score. Each pillar lists the inputs it reads; its
// score recomputes only once ALL of them have loaded this session, else the
// last complete value stands (from this session or the launch snapshot).
// Pure, unit-tested; src/Scorify.jsx marks inputs as they load or fail.
export const PILLAR_INPUTS = {
  finance:   ['finances', 'bills', 'goals', 'plaid_cache', 'debts'],
  health:    ['workouts', 'daily_activity', 'sleep', 'medications', 'checkups', 'supplements', 'goals', 'screen_time', 'habits'],
  habits:    ['habits', 'goals'],
  wellbeing: ['moods', 'journal'],
};
export const ALL_INPUTS = [...new Set(Object.values(PILLAR_INPUTS).flat())];

/** Every input of `pillarId` has loaded this session. */
export const pillarReady = (pillarId, loaded) => (PILLAR_INPUTS[pillarId] || []).every(k => loaded && loaded.has(k));
/** Some input of `pillarId` failed or timed out (and the pillar is not ready). */
export const pillarFailed = (pillarId, loaded, failed) => !pillarReady(pillarId, loaded) && (PILLAR_INPUTS[pillarId] || []).some(k => failed && failed.has(k));

/**
 * Settle what the screen shows.
 * @param {object} a
 * @param {Array}  a.computed   calcPillarScores().pillars from CURRENT state (may be partial)
 * @param {Set}    a.loaded     input keys loaded this session
 * @param {Set}    [a.failed]   input keys that failed / timed out
 * @param {object} [a.previous] { [pillarId]: lastCompletePillar } (session or launch snapshot)
 * @param {number} [a.now]
 * @returns {{ pillars: Array, lastComplete: object, pending: boolean }}
 *   pillars: one per computed pillar, each with { fresh, stale, pending, computedAt, failed }
 *   lastComplete: the updated map to keep (and persist in the launch snapshot)
 */
export function settlePillars({ computed = [], loaded = new Set(), failed = new Set(), previous = {}, now = Date.now() } = {}) {
  const lastComplete = { ...(previous || {}) };
  let pending = false;
  const pillars = (computed || []).map(p => {
    if (pillarReady(p.id, loaded)) {
      // computedAt moves only when the score moves, so re-renders never re-stamp it.
      const prev = lastComplete[p.id];
      const same = prev && prev.pct === p.pct && prev.earned === p.earned && prev.possible === p.possible && prev.computedAt;
      const fresh = { ...p, fresh: true, stale: false, pending: false, failed: false, computedAt: same ? prev.computedAt : now };
      lastComplete[p.id] = fresh;
      return fresh;
    }
    const prev = lastComplete[p.id];
    const didFail = pillarFailed(p.id, loaded, failed);
    if (!didFail) pending = true;
    if (prev) return { ...prev, fresh: false, stale: true, pending: !didFail, failed: didFail };
    // Nothing complete ever: an honest placeholder ("··" via pctText), never 0% or a partial number.
    return { ...p, pct: null, earned: 0, band: 'untracked', placeholder: true, fresh: false, stale: false, pending: !didFail, failed: didFail, computedAt: null };
  });
  return { pillars, lastComplete, pending };
}

/** "just now", "4m ago", "2h ago", "3d ago" for the "Last updated" line. */
export function relativeTime(ts, now = Date.now()) {
  const t = Number(ts); if (!Number.isFinite(t) || t <= 0) return null;
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** Keep only what the launch snapshot needs from a complete pillar. */
export const snapshotPillar = (p) => p ? { id: p.id, label: p.label, short: p.short, pct: p.pct, earned: p.earned, possible: p.possible, band: p.band, trackedCount: p.trackedCount, totalCount: p.totalCount, buckets: p.buckets, computedAt: p.computedAt } : null;
